import {
	BEHAVIOUR_PHYSICS,
	BEHAVIOUR_REPELL,
	REPELL_PUSH
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

import type { SpacesContext } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * RandomLerpLinesPhase — FORK of SpacesDepartment._randomLerpLines + the
 * randomLerpLines branch of SpacesParticleProcessor.
 *
 * VISUAL: every particle traces straight eased lines between waypoints
 * inside the box — first a random point, then a fixed 7-step chain (top
 * centre, random bottom, centre, a side edge, top/bottom coin flips, quarter
 * offsets) — additive blend with x/y ease 1, so the box fills with darting
 * light streaks; the mouse pushes streaks in a 200px PUSH radius.
 *
 * The reference chain lives in a per-particle onTargetReached closure with
 * captured `count`/`lastX` — here those are per-lane arrays and the closure
 * is a registered physics handler (counts past 7 arm nothing: the chain
 * ends). The frame constants are captured at arm time like the closures
 * (a mid-phase resize keeps the stale frame, reference behaviour).
 */
export class RandomLerpLinesPhase implements DepartmentPhase {
	public readonly name = 'RandomLerpLines';

	private _ctx: SpacesContext;
	private _handlerId = 0;

	//The closure state (lane - range.start):
	private _chainCount: Uint8Array;
	private _chainLastX: Float32Array;

	//The closures' frame constants, captured at arm time:
	private _x1 = 0;
	private _x2 = 0;
	private _w = 0;
	private _xc = 0;
	private _y1 = 0;
	private _y2 = 0;
	private _h = 0;
	private _yc = 0;

	constructor(ctx: SpacesContext) {
		this._ctx = ctx;
	}

	public init(): void {
		this._chainCount = new Uint8Array(this._ctx.range.count);
		this._chainLastX = new Float32Array(this._ctx.range.count);
		this._handlerId = this._ctx.registerPhysicsHandler((lane: number) => this._chainTargetReached(lane));
	}

	public start(): void {
		const frame = this._ctx.getFrameSizes();

		this._x1 = frame.x1 + 0.005;
		this._x2 = frame.x2 - 0.005;
		this._w = this._x2 - this._x1;
		this._xc = this._x1 + this._w / 2;
		this._y1 = frame.y1 + 0.01;
		this._y2 = frame.y2 - 0.01;
		this._h = this._y2 - this._y1;
		this._yc = this._y1 + this._h / 2;

		for (let n = 0; n < this._ctx.activeCount; n++) {
			this._lerpLineLane(this._ctx.range.start + n);
		}
	}

	public tick(): void {
		//All motion is kernel-side (the lerp machine drives the chains).
	}

	public hide(): void {
		//In-flight lerps carry over deactivated exactly like the reference
		//(see the pixel phase note); the handler ids on the lanes are
		//cleared by the next phases' resetLane arms.
	}

	public reset(): void {
		//No persistent phase state.
	}

	/** SpacesParticleProcessor.randomLerpLines, lane-for-line. */
	private _lerpLineLane(lane: number): void {
		const ctx = this._ctx;
		const core = ctx.core;

		ctx.resetLane(lane);
		ctx.deactivateAll(lane);

		core.behaviourMask[lane] |= BEHAVIOUR_PHYSICS;
		core.blendAdd[lane] = 1;

		ctx.setEaseXY(lane, 1, 1);

		//particlePhysics.reset():
		core.vx[lane] = 0;
		core.vy[lane] = 0;
		core.gravity[lane] = 0;

		this._chainCount[lane - ctx.range.start] = 1;
		ctx.physHandler[lane - ctx.range.start] = this._handlerId;

		ctx.lerpToPosition(lane, this._x1 + Math.random() * this._w, this._y1 + Math.random() * this._h, 400 + Math.random() * 1000);

		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		core.repellType[lane] = REPELL_PUSH;
		core.repellRadius[lane] = 200;
		core.repellPower[lane] = 100 + Math.random() * 200;
	}

	/** The onTargetReached chain (closure `count`/`lastX` -> lanes). */
	private _chainTargetReached(lane: number): void {
		const ctx = this._ctx;
		const slot = lane - ctx.range.start;
		const count = this._chainCount[slot];

		if (count === 1) {
			ctx.lerpToPosition(lane, this._xc, this._y1, 400 + Math.random() * 1000);
		}

		if (count === 2) {
			ctx.lerpToPosition(lane, this._x1 + Math.random() * this._w, this._y2, 400 + Math.random() * 1000);
		}

		if (count === 3) {
			ctx.lerpToPosition(lane, this._xc, this._yc, 400 + Math.random() * 1000);
		}

		if (count === 4) {
			const direction = Math.random() < 0.5 ? -1 : 1;
			const edgeX = (this._w / 2) * direction;

			ctx.lerpToPosition(lane, edgeX, this._yc, 400 + Math.random() * 1000);
		}

		if (count === 5) {
			const edgeY = Math.random() < 0.5 ? this._y1 : this._y2;

			ctx.lerpToPosition(lane, this._xc, edgeY, 400 + Math.random() * 1000);
		}

		if (count === 6) {
			let x = this._xc;
			const rand = Math.random();
			if (rand < 0.333333) x = this._x1 + this._w / 4;
			else if (rand < 0.666666) x = this._x2 - this._w / 4;

			ctx.lerpToPosition(lane, x, this._yc, 400 + Math.random() * 1000);

			this._chainLastX[slot] = x;
		}

		if (count === 7) {
			let x: number;
			const edgeY = Math.random() < 0.5 ? this._y1 : this._y2;

			if (Math.random() < 0.5) x = this._chainLastX[slot] - this._w / 8;
			else x = this._chainLastX[slot] + this._w / 8;

			ctx.lerpToPosition(lane, x, edgeY, 400 + Math.random() * 1000);

			this._chainLastX[slot] = x;
		}

		this._chainCount[slot] = count + 1;
	}
}
